// src/components/CSVUploadPatients.js
import React, { useState, useRef } from 'react';
import { Upload, CheckCircle, XCircle, AlertCircle, FileText, Download, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts';

const PATIENT_FIELDS = [
  { key: 'patient_id', label: 'Patient ID', required: false },
  { key: 'first_name', label: 'First Name', required: true },
  { key: 'last_name', label: 'Last Name', required: true },
  { key: 'middle_name', label: 'Middle Name', required: false },
  { key: 'date_of_birth', label: 'Date of Birth', required: true },
  { key: 'gender', label: 'Gender', required: false },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'email', label: 'Email', required: false },
  { key: 'address', label: 'Address', required: false },
  { key: 'city', label: 'City', required: false },
  { key: 'state', label: 'State', required: false },
  { key: 'zip', label: 'ZIP Code', required: false },
  { key: 'insurance_primary_carrier', label: 'Insurance Carrier', required: false },
  { key: 'insurance_primary_plan', label: 'Insurance Plan', required: false },
  { key: 'insurance_member_id', label: 'Member ID', required: false },
  { key: 'insurance_group_number', label: 'Group Number', required: false },
];

const CSVUploadPatients = ({ practiceId, onUploadComplete }) => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [step, setStep] = useState('upload'); // upload | mapping | importing | done
  const [csvText, setCsvText] = useState('');
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const processFile = async (selectedFile) => {
    setFile(selectedFile);
    setErrors([]);
    setResults(null);

    const text = await selectedFile.text();
    setCsvText(text);

    const { getCSVHeaders, autoDetectPatientMapping } = await import('../services/csvParserService');
    const { headers, preview } = getCSVHeaders(text);
    setCsvHeaders(headers);
    setPreviewRows(preview);
    const detected = autoDetectPatientMapping(headers);
    setFieldMapping(detected);
    setStep('mapping');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f && (f.type === 'text/csv' || f.name.endsWith('.csv'))) processFile(f);
    else setErrors(['Please upload a CSV file']);
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f && (f.type === 'text/csv' || f.name.endsWith('.csv'))) processFile(f);
    else setErrors(['Please upload a CSV file']);
  };

  const handleUpload = async () => {
    setUploading(true);
    setProgress(0);
    setErrors([]);
    setStep('importing');

    try {
      const { parsePatientCSVWithMapping } = await import('../services/csvParserService');
      const parsedData = parsePatientCSVWithMapping(csvText, fieldMapping);
      setProgress(40);

      if (parsedData.errors.length > 0) {
        setErrors(parsedData.errors);
        setStep('mapping');
        setUploading(false);
        return;
      }

      const { batchImportPatients, logImportActivity } = await import('../services/dataImportService');
      const importResults = await batchImportPatients(parsedData.data, practiceId, 50);
      setProgress(80);

      if (user?.id && practiceId) {
        await logImportActivity(practiceId, user.id, 'patients', importResults);
      }
      setProgress(100);
      setResults(importResults);
      setStep('done');

      if (onUploadComplete) onUploadComplete(importResults);
    } catch (error) {
      setErrors([error.message || 'An error occurred during upload']);
      setStep('mapping');
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setStep('upload');
    setCsvText('');
    setCsvHeaders([]);
    setPreviewRows([]);
    setFieldMapping({});
    setResults(null);
    setErrors([]);
  };

  const downloadTemplate = () => {
    const template = `patient_id,first_name,last_name,middle_name,date_of_birth,gender,phone,email,address,city,state,zip,insurance_primary_carrier,insurance_primary_plan,insurance_member_id,insurance_group_number
PT-001,John,Smith,Michael,1975-03-15,M,555-123-4567,john.smith@email.com,123 Main St,Holland,MI,49423,Delta Dental,PPO Plus,DD123456789,GRP-8472
PT-002,Jane,Doe,Ann,1982-07-22,F,555-987-6543,jane.doe@email.com,456 Oak Ave,Grand Rapids,MI,49501,MetLife,Standard,ML987654321,GRP-1234`;
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'patient_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const requiredMapped = PATIENT_FIELDS.filter(f => f.required).every(f => fieldMapping[f.key]);

  // ── Upload step ──────────────────────────────────────────────────────────────
  if (step === 'upload') {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px', color: '#1e293b' }}>
            Import Patient Data
          </h2>
          <p style={{ color: '#64748b', marginBottom: '20px' }}>
            Upload a CSV from any PMS system — we'll auto-detect your columns.
          </p>
          <button onClick={downloadTemplate} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', color: '#475569' }}>
            <Download size={16} />
            Download FlowRCM Template
          </button>
        </div>

        <div
          onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{ border: dragActive ? '2px dashed #14b8a6' : '2px dashed #cbd5e1', borderRadius: '12px', padding: '60px 40px', textAlign: 'center', cursor: 'pointer', backgroundColor: dragActive ? '#f0fdfa' : '#f8fafc', transition: 'all 0.2s', marginBottom: '20px' }}
        >
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />
          <Upload size={48} style={{ margin: '0 auto 16px', color: '#94a3b8' }} />
          <p style={{ fontWeight: '500', marginBottom: '8px', color: '#1e293b' }}>Drop your CSV here, or click to browse</p>
          <p style={{ color: '#64748b', fontSize: '14px' }}>Works with Eaglesoft, Dentrix, CareStack, Open Dental, and any CSV export</p>
        </div>

        {errors.length > 0 && (
          <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px' }}>
            {errors.map((e, i) => <p key={i} style={{ color: '#991b1b' }}>{e}</p>)}
          </div>
        )}
      </div>
    );
  }

  // ── Mapping step ─────────────────────────────────────────────────────────────
  if (step === 'mapping') {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '6px', color: '#1e293b' }}>
            Map Your Columns
          </h2>
          <p style={{ color: '#64748b' }}>
            We auto-detected your column names from <strong>{file?.name}</strong>. Confirm or adjust the mapping below.
          </p>
        </div>

        {errors.length > 0 && (
          <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <XCircle size={18} style={{ color: '#dc2626' }} />
              <span style={{ fontWeight: '500', color: '#dc2626' }}>Fix these errors and try again</span>
            </div>
            {errors.slice(0, 5).map((e, i) => <p key={i} style={{ color: '#991b1b', fontSize: '14px' }}>{e}</p>)}
            {errors.length > 5 && <p style={{ color: '#991b1b', fontSize: '14px' }}>...and {errors.length - 5} more</p>}
          </div>
        )}

        <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', marginBottom: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 40px 1fr 140px', gap: '0', backgroundColor: '#f8fafc', padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>FlowRCM Field</span>
            <span />
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Your CSV Column</span>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Sample Value</span>
          </div>

          {PATIENT_FIELDS.map((field) => {
            const mappedCol = fieldMapping[field.key];
            const sampleVal = mappedCol && previewRows[0] ? previewRows[0][mappedCol] : '';
            return (
              <div key={field.key} style={{ display: 'grid', gridTemplateColumns: '1fr 40px 1fr 140px', gap: '0', padding: '10px 16px', borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: field.required ? '600' : '400' }}>
                  {field.label}
                  {field.required && <span style={{ color: '#dc2626', marginLeft: '4px' }}>*</span>}
                </span>
                <ArrowRight size={16} style={{ color: '#94a3b8', margin: '0 auto' }} />
                <select
                  value={mappedCol || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFieldMapping(prev => {
                      const next = { ...prev };
                      if (val) next[field.key] = val;
                      else delete next[field.key];
                      return next;
                    });
                  }}
                  style={{ padding: '6px 8px', border: `1px solid ${mappedCol ? '#5eead4' : field.required ? '#fca5a5' : '#e2e8f0'}`, borderRadius: '6px', fontSize: '13px', backgroundColor: mappedCol ? '#f0fdfa' : 'white', color: '#1e293b', width: '100%' }}
                >
                  <option value="">— Skip —</option>
                  {csvHeaders.map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
                <span style={{ fontSize: '12px', color: '#94a3b8', paddingLeft: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {sampleVal || ''}
                </span>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={reset} style={{ padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer', color: '#64748b', fontSize: '15px' }}>
            Back
          </button>
          <button
            onClick={handleUpload}
            disabled={!requiredMapped}
            style={{ flex: 1, padding: '12px', backgroundColor: requiredMapped ? '#14b8a6' : '#94a3b8', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '500', cursor: requiredMapped ? 'pointer' : 'not-allowed' }}
          >
            {requiredMapped ? 'Import Patients' : 'Map required fields to continue'}
          </button>
        </div>
      </div>
    );
  }

  // ── Importing step ───────────────────────────────────────────────────────────
  if (step === 'importing') {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{ width: `${progress}%`, height: '100%', backgroundColor: '#14b8a6', transition: 'width 0.3s' }} />
        </div>
        <p style={{ color: '#64748b' }}>Importing patients... {progress}%</p>
      </div>
    );
  }

  // ── Done step ────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '24px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <CheckCircle size={24} style={{ color: '#14b8a6' }} />
          <span style={{ fontWeight: '600', fontSize: '18px', color: '#0f766e' }}>Import Complete!</span>
        </div>
        <p style={{ color: '#0f766e', marginBottom: '6px' }}>✓ Successfully imported: <strong>{results?.successful}</strong> patients</p>
        {results?.failed > 0 && <p style={{ color: '#dc2626', marginBottom: '6px' }}>✗ Failed: <strong>{results.failed}</strong></p>}
        {results?.duplicates > 0 && <p style={{ color: '#f59e0b', marginBottom: '6px' }}>⚠ Duplicates skipped: <strong>{results.duplicates}</strong></p>}
        <button onClick={reset} style={{ marginTop: '12px', padding: '8px 16px', backgroundColor: 'white', border: '1px solid #5eead4', borderRadius: '6px', cursor: 'pointer', color: '#14b8a6', fontWeight: '500' }}>
          Import Another File
        </button>
      </div>
    </div>
  );
};

export default CSVUploadPatients;
